import React from "react";
import { Button } from "antd";
import { useAsyncAction } from "../../hooks/useAsyncAction";

/**
 * Plain <button> (keeps classes like "gradient-button") that shows a spinner
 * and is disabled while loading.
 * - `loading`: set it from the page (e.g. form submit state)
 * - `onClick`: if it returns a promise, the button loads until it settles
 */
const LoadingButton = ({
  loading = false,
  disabled = false,
  onClick,
  children,
  type = "button",
  className = "",
  ...rest
}) => {
  const [run, running] = useAsyncAction(onClick);
  const isLoading = loading || running;

  return (
    <button
      {...rest}
      type={type}
      className={`${className} loading-button${isLoading ? " is-loading" : ""}`}
      disabled={disabled || isLoading}
      aria-busy={isLoading}
      onClick={onClick ? run : undefined}
    >
      {isLoading && <span className="btn-spinner" aria-hidden="true" />}
      {children}
    </button>
  );
};

/**
 * antd Button whose `onClick` promise drives its built-in `loading` state
 * (antd shows a spinner and ignores clicks while loading).
 */
export const AntLoadingButton = ({ loading = false, onClick, ...rest }) => {
  const [run, running] = useAsyncAction(onClick);
  return (
    <Button
      {...rest}
      loading={loading || running}
      onClick={onClick ? run : undefined}
    />
  );
};

export default LoadingButton;
